/**
 * Ops/Admin scoped theme preference.
 *
 * Three-state preference: "auto" | "light" | "dark".
 *
 * * `preference`       — what the user picked (or the resolved fallback).
 * * `effectiveTheme`   — what should actually paint right now:
 *                        "light" | "dark". When `preference` is
 *                        "auto", this tracks the OS via
 *                        `prefers-color-scheme`.
 * * `setPreference()`  — update + persist.
 *
 * Persisted under localStorage key `herborist_ops_theme`. Backward
 * compatible:
 *
 *   - legacy "light"   → preference="light",  effective=light
 *   - legacy "dark"    → preference="dark",   effective=dark
 *   - missing key      → preference="auto",   effective follows OS
 *   - any other value  → preference="auto"   (safe fallback)
 *
 * The hook never touches `document.documentElement` / `<body>`. Each
 * consumer wraps its OWN layout root with the `dark` class, keeping
 * storefront / checkout / login / account pages entirely unaffected.
 */
import { useState, useEffect, useCallback } from 'react';

export const ADMIN_THEME_KEY = 'herborist_ops_theme';

const VALID_PREFERENCES = ['auto', 'light', 'dark'];

function readStoredPreference() {
  try {
    const raw = window.localStorage.getItem(ADMIN_THEME_KEY);
    if (VALID_PREFERENCES.includes(raw)) return raw;
  } catch { /* ignore */ }
  return 'auto';
}

function systemPrefersDark() {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

function resolveEffective(preference, systemIsDark) {
  if (preference === 'dark') return 'dark';
  if (preference === 'light') return 'light';
  return systemIsDark ? 'dark' : 'light';
}

export function useAdminTheme() {
  const [preference, setPreferenceState] = useState(readStoredPreference);
  const [systemIsDark, setSystemIsDark] = useState(systemPrefersDark);

  // Persist preference. Storage failures are non-fatal (e.g. private
  // browsing throwing on quota).
  useEffect(() => {
    try { window.localStorage.setItem(ADMIN_THEME_KEY, preference); }
    catch { /* ignore */ }
  }, [preference]);

  // Live-track OS theme changes while preference === "auto".
  // Listener is always installed (cheap) so toggling back to auto
  // immediately reflects the current OS state.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => setSystemIsDark(e.matches);
    // Safari <14 used the deprecated addListener/removeListener API.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  const setPreference = useCallback((next) => {
    setPreferenceState(VALID_PREFERENCES.includes(next) ? next : 'auto');
  }, []);

  const effectiveTheme = resolveEffective(preference, systemIsDark);

  return {
    preference,
    effectiveTheme,
    setPreference,
    isAuto: preference === 'auto',
  };
}
